// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// Component imports
import { DxDataGridComponent, DxFormComponent, DxPopupComponent } from 'devextreme-angular';
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// 3rd party imports
import { cloneDeep } from 'lodash';
import notify from 'devextreme/ui/notify';
import { custom } from 'devextreme/ui/dialog';

// Service imports
import { UserService } from '../../../../_services/data-services/user.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { CashReceiptService } from '../../_services/cash-management/cash-receipt.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';

// Model imports
import { BaseParameterDto } from '../../../../_models/base-parameter-dto';
import { UserInfoResultDto } from '../../../../_models/user-info-result-dto';
import { CashReceiptItemRawDto } from '../../_models/cash-management/cash-receipts/cash-receipt-item-raw-dto';
import { CashReceiptPaymentTypeResultDto } from '../../_models/cash-management/cash-receipts/cash-receipt-payment-type-dto';
import { CashReceiptRawDto } from '../../_models/cash-management/cash-receipts/cash-receipt-raw-dto';
import { CashReceiptResultDto } from '../../_models/cash-management/cash-receipts/cash-receipt-result-dto';
import { DimensionSelectorResultDto } from '../../_models/common/dimensions/dimension-selector-result-dto';
import { GLAccountSelectorResultDto } from '../../_models/common/gl-account/glaccount-selector-result-dto';
import { CashReceiptParametersDto } from '../../_models/cash-management/cash-receipts/cash-receipt-parameters-dto';
import { CashReceiptEditorResultDto } from '../../_models/cash-management/cash-receipts/cash-receipt-editor-result-dto';
import { SubCategoryByGlAccountParametersDto } from '../../_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { DimensionOptionCodeRawDto } from '../../_models/common/dimensions/dimension-option-code-raw-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';

// Constants imports
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { ActionConstants } from '../../../../_constants/action-constants';

@Component({
    selector: 'app-cash-receipts-editor',
    templateUrl: './cash-receipts-editor.component.html',
    styleUrls: ['./cash-receipts-editor.component.scss']
})
export class CashReceiptsEditorComponent implements OnInit {
    @ViewChild('editorCashReceipts', { static: false }) editorCashReceipts: DxPopupComponent;
    @ViewChild('editorForm', { static: false }) editorForm: DxFormComponent;
    @ViewChild('cashReceiptEditorGrid', { static: false }) cashReceiptEditorGrid: DxDataGridComponent;
    @ViewChild('cashReceiptEditorMessageBox', { static: false }) cashReceiptEditorMessageBox: MessageBoxComponent;

    @Input() visible = false;
    @Input() cashReceipt: CashReceiptResultDto;
    @Input() title: string;
    @Input() cashReceiptParams: CashReceiptParametersDto;

    @Output() onCashReceiptClose = new EventEmitter<boolean>();

    cashReceiptRawItem: CashReceiptRawDto; // CashReceipt Item
    paymentType: CashReceiptPaymentTypeResultDto[] = [];
    selectedPaymentType: CashReceiptPaymentTypeResultDto;
    originalCashReceipt: CashReceiptRawDto;

    offsetGLAccountNumberSource: GLAccountSelectorResultDto[] = [];
    subCategorySource: GLSubcategorySelectorResultDto[] = [];
    dimensionSource: DimensionSelectorResultDto[] = [];
    entityUsesSubcategory: boolean;
    defaultBankName: string;

    loadingVisible = false;
    isAllowEdit = false;
    isAllowLimitedEdit = false;
    isAllowGLAccountEdit = false;
    isDeleteAllow = false;
    disableGLAccountEditor = false;
    deletePopupVisible = false;
    isInternalUser = false;
    userInfo: UserInfoResultDto;

    gridKeyExpr: string;
    columnDefs: object[];
    dateFormat: string;

    displayTransactionLogPopUp = false;
    displayCashReceiptReverseEditor = false;
    transactionLogTitle: string;
    transactionLogResultData: TransactionLogResultDto[] = [];
    modifiedCashReceiptLineItems: CashReceiptItemRawDto[];

    currencyFormat: string;

    glAccountColumns: Array<any> = [];

    iconDelete = IconConstants.DELETE;
    iconClose = IconConstants.CROSS;
    iconSave = IconConstants.SAVE;
    iconPlus = IconConstants.ADD;
    iconHistory = IconConstants.HISTORY;
    iconReverse = IconConstants.REVERSE;

    constructor(
        private readonly cashReceiptService: CashReceiptService,
        private readonly errorService: ServerErrorService,
        private readonly userService: UserService,
        private readonly precisionService: PrecisionService,
        private readonly helperService: HelperService
    ) {
        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
    }

    ngOnInit(): void {
        this.dateFormat = StringFormatConstants.dateFormat;
        this.currencyFormat = this.precisionService.getCurrencyFormat();

        this.isInternalUser = this.userService.isInternal();
        this.userInfo = this.userService.getCurrentUserInfo();

        this.cashReceiptRawItem = new CashReceiptRawDto();
        this.prepareGLAccountColumns();
        this.populateCREditor(true);
        this.getPaymentTypeList();
    }

    onShown(e: any): void {
        e.component.repaint();
    }

    onHidden(): void {
        this.visible = false;
        this.closeCashReceiptEditor(false);
    }

    addNewLineItem() {
        this.addRawLineItem();
    }

    onCashReceiptSave() {
        const validator = this.editorForm.instance.validate();
        if (validator.isValid) {
            this.loadingVisible = true;
            if (Utils.notNullOrEmpty(this.cashReceiptRawItem.items)) {
                this.cashReceiptRawItem.items.forEach((rowItem) => {
                    const dimensions: DimensionOptionCodeRawDto[] = [];
                    this.dimensionSource.forEach((dimension) => {
                        const dim: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                        dim.dimensionCode = dimension.code;
                        dim.dimensionOptionCode = rowItem[dimension.code];
                        dimensions.push(dim);
                    });
                    rowItem.dimensions = dimensions;
                });
                this.cashReceiptEditorGrid.instance.saveEditData().then(() => {
                    const isLineItemPrepared = this.prepareCashReceipt();
                    if (Utils.isTrue(isLineItemPrepared)) {
                        const resultStatus = this.checkLineItemValidity(this.modifiedCashReceiptLineItems);
                        if (resultStatus.isValid) {
                            this.saveCashReceipt(this.modifiedCashReceiptLineItems);
                        } else {
                            this.loadingVisible = false;
                            this.cashReceiptEditorMessageBox.showErrorMessage('Error', resultStatus.message);
                            this.resetCashReceiptRawItem();
                        }
                    }
                });
            } else {
                this.cashReceiptEditorMessageBox.showWarningMessage('Warning', 'At least one line item is required');
            }
        }
    }

    onReverseCashReceipt() {
        this.cashReceiptParams.id = this.cashReceipt.cmCashReceiptId;
        this.displayCashReceiptReverseEditor = true;
    }

    onDeleteCashReceipt(): void {
        const customDialog = custom({
            title: 'Delete Cash Receipt',
            messageHtml: `<b> Are you sure to delete cash receipt #${this.cashReceipt.cmCashReceiptId}?</b>`,
            buttons: [
                {
                    type: 'danger',
                    text: 'Ok',
                    onClick: (e) => {
                        this.deleteCashReceipt();
                    }
                },
                {
                    type: 'default',
                    text: 'Cancel',
                    onClick: (e) => {
                        return;
                    }
                }
            ]
        });
        customDialog.show();
    }

    onDeleteCRFromGrid(item: CashReceiptItemRawDto): void {
        if (!item.cmCashReceiptItemId) {
            this.cashReceiptRawItem.items = this.cashReceiptRawItem.items.filter((d) => d['itemId'] !== item['itemId']);
        } else {
            this.cashReceiptRawItem.items.forEach((ele) => {
                if (ele.cmCashReceiptItemId === item.cmCashReceiptItemId) {
                    ele.crudAction = ActionConstants.DELETE;
                }
            });
        }

        this.setDeleteLineItemFlag();

        this.cashReceiptEditorGrid.instance.filter(['crudAction', '<>', ActionConstants.DELETE]);
        this.cashReceiptEditorGrid.instance.refresh();
    }

    onOffsetGLAccountSelection(account: GLAccountSelectorResultDto[], cellInfo: any): void {
        const oldAccount = Utils.notNullAndDefined(cellInfo.displayValue) ? cellInfo.displayValue.split(';')[0] : '';
        if (Utils.notNullOrEmpty(account) && Utils.notEqualsIgnoreCase(oldAccount, account[0].glAccountNumber)) {
            cellInfo.setValue(account[0].glAccountNumber);
            if (this.entityUsesSubcategory) {
                this.populateSubcategories(account[0].glAccountNumber, cellInfo.data);
            }
            cellInfo.displayValue = account[0].displayText;
        }
    }

    onEditorContentReady(event): void {
        event.component.filter(['crudAction', '<>', ActionConstants.DELETE]);
    }

    onRowUpdated(event): void {
        const rowData = event.data;
        const dimensions: DimensionOptionCodeRawDto[] = [];
        this.cashReceiptRawItem.items.forEach((item) => {
            if (item.cmCashReceiptItemId === rowData.cmCashReceiptItemId && item['itemId'] === rowData.itemId) {
                this.dimensionSource.forEach((row) => {
                    const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                    obj.dimensionCode = row.code;
                    obj.dimensionOptionCode = rowData[row.code];
                    dimensions.push(obj);
                });
                item.dimensions = dimensions;
                if (!item.crudAction) {
                    item.crudAction = ActionConstants.EDIT;
                }
            }
        });
    }

    onTransactioLogClick(rec: CashReceiptRawDto) {
        if (rec.cmCashReceiptId) {
            this.transactionLogTitle = 'Log of Cash Receipt ' + rec.cmCashReceiptId;
            this.populateTransactionLog(rec.cmCashReceiptId);
            this.displayTransactionLogPopUp = true;
        }
    }

    onTransactionLogClose(e: any) {
        this.displayTransactionLogPopUp = false;
    }

    onReverseEditorClose(refresh: boolean): void {
        this.cashReceiptParams.id = null;
        this.displayCashReceiptReverseEditor = false;
        this.closeCashReceiptEditor(refresh);
    }

    calculateCustomSummary(e): void {
        this.helperService.calculateTotalSummary(e);
    }

    onSubCategoryChanged(event: any, cellInfo: any): void {
        cellInfo.setValue(event.value);
    }

    // As we have added to avoid clear payment method field as per V1
    onPaymentMethodClick(e: any): void {
        if (Utils.notNullAndDefined(e.event)) {
            if (Utils.isNullOrUndefined(e.value)) {
                e.component.option('value', e.previousValue);
            }
        }
    }

    private populateTransactionLog(transactionId: number): void {
        const baseParam: TransactionLogParameterDto = new TransactionLogParameterDto();
        baseParam.transactionId = transactionId;
        baseParam.clearCache = true;
        this.loadingVisible = true;
        this.cashReceiptService.getTransactionLog(baseParam).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogResultData = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getPaymentTypeList(): void {
        const param: BaseParameterDto = new BaseParameterDto();
        param.clearCache = true;

        this.cashReceiptService.getPaymentType(param).subscribe(
            (result: CashReceiptPaymentTypeResultDto[]) => {
                this.paymentType = result;
                this.setSelectedPaymentType();
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private setSelectedPaymentType(): void {
        if (this.paymentType.length > 0) {
            if (this.cashReceiptRawItem && this.cashReceiptRawItem.paymentMethod) {
                this.selectedPaymentType = this.paymentType.find(
                    (d) => d.code === this.cashReceiptRawItem.paymentMethod
                );
            }
        }
    }

    private saveCashReceipt(modifiedCashReceiptLineItems: CashReceiptItemRawDto[]): void {
        if (!this.cashReceipt) {
            this.addCashReceipt(modifiedCashReceiptLineItems);
        } else {
            this.editCashReceipt(modifiedCashReceiptLineItems);
        }
    }

    private addCashReceipt(modifiedCashReceiptLineItems: CashReceiptItemRawDto[]): void {
        let cashReceiptRawItem: CashReceiptRawDto = new CashReceiptRawDto();
        cashReceiptRawItem = Object.assign(cashReceiptRawItem, this.cashReceiptRawItem);
        cashReceiptRawItem.items = modifiedCashReceiptLineItems;
        this.cashReceiptService.addCashReceipt(cashReceiptRawItem).subscribe(
            (response: string) => {
                this.cashEditorSuccessMessage(response);
                this.loadingVisible = false;
            },
            (error) => {
                this.cashEditorErrorMessage(error);
                this.loadingVisible = false;
            }
        );
    }

    private editCashReceipt(modifiedCashReceiptLineItems: CashReceiptItemRawDto[]): void {
        let cashReceiptRawItem: CashReceiptRawDto = new CashReceiptRawDto();
        cashReceiptRawItem = Object.assign(cashReceiptRawItem, this.cashReceiptRawItem);
        cashReceiptRawItem.items = modifiedCashReceiptLineItems;
        cashReceiptRawItem.items.forEach((item) => {
            item.crudAction = item.crudAction || '';
            item.dimensions = item.dimensions.filter((d) => !!d.dimensionOptionCode);
        });

        this.cashReceiptService.editCashReceipt(cashReceiptRawItem).subscribe(
            (response: string) => {
                this.cashEditorSuccessMessage(response);
                this.loadingVisible = false;
            },
            (error) => {
                this.cashEditorErrorMessage(error);
                this.loadingVisible = false;
            }
        );
    }

    private cashEditorSuccessMessage(response: string): void {
        notify(response, 'Success', MessageInterval.successMsgInterval);
        this.closeCashReceiptEditor(true);
    }

    private cashEditorErrorMessage(error): void {
        this.resetCashReceiptRawItem();
        this.errorService.displayError(error);
    }

    private resetCashReceiptRawItem(): void {
        this.prepareDimensions();
        if (this.entityUsesSubcategory) {
            this.getSubCategories();
        }
    }

    private prepareCashReceipt(): boolean {
        let isLineItemPrepared = true;
        const dimensionKeysToDelete: string[] = [];
        const dimensionToDelete: any[] = [];
        let totalAmount = 0;
        const modifiedCashReceiptLineItems: CashReceiptItemRawDto[] = [];
        if (this.cashReceiptRawItem.items && this.cashReceiptRawItem.items.length > 0) {
            for (const cashReceiptRawItem of this.cashReceiptRawItem.items) {
                let rawItem = Object.assign(new CashReceiptItemRawDto(), cashReceiptRawItem);

                if (Utils.equals(rawItem.crudAction, ActionConstants.DELETE)) {
                    rawItem = this.originalCashReceipt.items.find((c) =>
                        Utils.equals(c['itemId'], cashReceiptRawItem['itemId'])
                    );
                    rawItem.crudAction = ActionConstants.DELETE;
                } else {
                    rawItem.glAccountNumber = rawItem.offsetGLAccountNumber;
                    if (rawItem.amount > 0) {
                        totalAmount += rawItem.crudAction !== ActionConstants.DELETE ? rawItem.amount : 0;
                        if (Utils.notNullOrEmpty(rawItem.dimensions)) {
                            rawItem.dimensions.forEach((dim) => {
                                const isDimRequired = this.isDimensionMandatory(dim);
                                dim.dimensionOptionCode = rawItem[dim.dimensionCode];
                                dimensionKeysToDelete.push(dim.dimensionCode);

                                if (!isDimRequired && !dim.dimensionOptionCode) {
                                    dimensionToDelete.push(dim.dimensionCode);
                                }
                            });

                            dimensionToDelete.forEach((dim) => {
                                const rawIndex = rawItem.dimensions.findIndex((d) =>
                                    Utils.equalsIgnoreCase(d.dimensionCode, dim)
                                );
                                if (rawIndex > -1) {
                                    rawItem.dimensions.splice(rawIndex);
                                }
                            });
                        }
                        dimensionKeysToDelete.forEach((key) => {
                            delete rawItem[key];
                        });
                        delete rawItem['subCategory'];
                        delete rawItem['subCategorySource'];
                    } else {
                        this.cashReceiptEditorMessageBox.showErrorMessage(
                            'Error',
                            'Line Item Amount must be different from zero'
                        );
                        isLineItemPrepared = false;
                        break;
                    }
                }

                rawItem.id = this.userInfo.userId;
                rawItem.displayName = this.userInfo.displayName;
                rawItem.updatedTime = new Date();

                modifiedCashReceiptLineItems.push(rawItem);
            }
        }
        if (isLineItemPrepared) {
            this.cashReceiptRawItem.amount = this.precisionService.getRoundedNumber(totalAmount);
            this.modifiedCashReceiptLineItems = modifiedCashReceiptLineItems;
        } else {
            this.loadingVisible = false;
            this.resetCashReceiptRawItem();
        }
        return isLineItemPrepared;
    }

    private isDimensionMandatory(dimensionToCheck: DimensionOptionCodeRawDto): boolean {
        const dimension = this.dimensionSource.find((d) =>
            Utils.equalsIgnoreCase(d.code, dimensionToCheck.dimensionCode)
        );
        return dimension.required;
    }

    private deleteCashReceipt(): void {
        const baseParams: CashReceiptParametersDto = new CashReceiptParametersDto();
        baseParams.managementCompanyId = this.cashReceipt.managementCompanyId;
        baseParams.communityId = this.cashReceipt.communityId;
        baseParams.id = this.cashReceipt.cmCashReceiptId;

        this.loadingVisible = true;
        this.cashReceiptService.deleteCashReceipt(baseParams).subscribe(
            (response: string) => {
                this.cashEditorSuccessMessage(response);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private closeCashReceiptEditor(clearCache = false): void {
        this.editorCashReceipts.visible = false;
        this.onCashReceiptClose.emit(clearCache);
    }

    private populateCREditor(clearCache = false): void {
        const baseParams: CashReceiptParametersDto = new CashReceiptParametersDto();
        baseParams.managementCompanyId = this.cashReceipt
            ? this.cashReceipt.managementCompanyId
            : this.cashReceiptParams.managementCompanyId;
        baseParams.communityId = this.cashReceipt ? this.cashReceipt.communityId : this.cashReceiptParams.communityId;
        baseParams.id = this.cashReceipt ? this.cashReceipt.cmCashReceiptId : null;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;
        this.cashReceiptService.getEditor(baseParams).subscribe(
            (result: CashReceiptEditorResultDto) => {
                this.offsetGLAccountNumberSource = result.glAccounts;
                this.dimensionSource = result.glDimensions;
                this.entityUsesSubcategory = result.entityUsesSubcategory;
                this.defaultBankName = result.defaultBankName;

                this.cashReceiptRawItem = result.cashReceipt;

                this.setAllowFlags();
                this.addSequence();

                if (this.cashReceiptRawItem.cmCashReceiptId) {
                    this.setSelectedPaymentType();
                    this.prepareDimensions();
                } else {
                    this.cashReceiptRawItem.receiveDate = new Date();
                    this.cashReceiptRawItem.postDate = new Date();
                    this.addRawLineItem();
                }

                if (this.entityUsesSubcategory) {
                    this.getSubCategories();
                }
                this.originalCashReceipt = cloneDeep(this.cashReceiptRawItem);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private addSequence(): void {
        this.cashReceiptRawItem.items.forEach((item) => {
            item['itemId'] = this.cashReceiptRawItem.items.indexOf(item);
        });
    }

    private setAllowFlags(): void {
        this.isAllowEdit = this.cashReceiptRawItem.cmCashReceiptId ? this.cashReceipt.allowEdit : true;
        this.isAllowLimitedEdit = this.cashReceiptRawItem.cmCashReceiptId ? this.cashReceipt.allowLimitedEdit : false;
        this.isAllowGLAccountEdit = this.cashReceiptRawItem.cmCashReceiptId
            ? this.cashReceipt.allowEditGLAccount
            : false;
        this.disableGLAccountEditor = !this.isAllowEdit;
        if (Utils.isTrue(this.disableGLAccountEditor)) {
            this.disableGLAccountEditor = !this.isAllowGLAccountEdit;
        }
        if (Utils.isTrue(this.isAllowLimitedEdit)) {
            this.title = `${this.title} (Limited Edit)`;
        }

        this.setDeleteLineItemFlag();
    }

    private populateSubcategories(
        offsetGLAccountNumber: string,
        rawItem: any,
        selectedSubcategoryCode: string = null,
        clearCache = false
    ): void {
        const baseParam: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        baseParam.managementCompanyId = this.cashReceipt
            ? this.cashReceipt.managementCompanyId
            : this.cashReceiptParams.managementCompanyId;
        baseParam.communityId = this.cashReceipt ? this.cashReceipt.communityId : this.cashReceiptParams.communityId;
        baseParam.glAccountNumber = offsetGLAccountNumber;
        baseParam.clearCache = clearCache;
        this.cashReceiptService.getSubCategories(baseParam).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                rawItem.subCategorySource = result;
                if (Utils.notNullOrEmpty(rawItem.subCategorySource)) {
                    const selectedSubCategory = rawItem.subCategorySource.find((d) =>
                        Utils.equalsIgnoreCase(d.code, selectedSubcategoryCode)
                    );
                    if (Utils.notNullAndDefined(selectedSubCategory)) {
                        rawItem.subCategoryCode = selectedSubCategory.code;
                    } else {
                        const defaultCat = rawItem.subCategorySource.find((s) => Utils.isTrue(s.isDefault));
                        rawItem.subCategoryCode = Utils.notNullAndDefined(defaultCat) ? defaultCat.code : null;
                    }
                } else {
                    rawItem.subCategoryCode = null;
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private addRawLineItem(): void {
        const item: CashReceiptItemRawDto = new CashReceiptItemRawDto();
        item.amount = 0;
        item['itemId'] =
            this.cashReceiptRawItem.items.length > 0
                ? Math.max.apply(
                      Math,
                      this.cashReceiptRawItem.items.map((o) => {
                          return o['itemId'];
                      })
                  ) + 1
                : 1;
        item.crudAction = 'add';
        item.cmCashReceiptId = this.cashReceiptRawItem.cmCashReceiptId;
        item.description = this.cashReceiptRawItem.description;

        this.cashReceiptRawItem.items.push(item);
        this.setDeleteLineItemFlag();

        this.prepareDimensions();
    }

    private getSubCategories(): void {
        if (this.cashReceiptRawItem.items && this.cashReceiptRawItem.items.length > 0) {
            this.cashReceiptRawItem.items.forEach((item) => {
                if (item.offsetGLAccountNumber) {
                    this.populateSubcategories(item.offsetGLAccountNumber, item, item.subCategoryCode);
                }
            });
        }
    }

    private prepareDimensions(): void {
        if (this.cashReceiptRawItem.items && this.cashReceiptRawItem.items.length > 0) {
            this.cashReceiptRawItem.items.forEach((item) => {
                const dimensions: DimensionOptionCodeRawDto[] = [];
                this.dimensionSource.forEach((dimNode) => {
                    let itemDimension: DimensionOptionCodeRawDto;

                    if (item.dimensions && item.dimensions.length > 0) {
                        itemDimension = item.dimensions.find((d) => d.dimensionCode === dimNode.code);
                    }
                    if (itemDimension) {
                        if (itemDimension.dimensionOptionCode) {
                            const selectedNode = dimNode.options.find(
                                (d) => d.code === itemDimension.dimensionOptionCode
                            );
                            item[itemDimension.dimensionCode] = selectedNode ? selectedNode.code : null;
                        } else if (!item[itemDimension.dimensionCode]) {
                            item[itemDimension.dimensionCode] = null;
                        }
                    } else {
                        const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                        obj.dimensionCode = dimNode.code;
                        obj.dimensionOptionCode = null;
                        dimensions.push(obj);
                        item[dimNode.code] = null;
                        item.dimensions = dimensions;
                    }
                });
            });
        }
    }

    private setDeleteLineItemFlag(): void {
        this.isDeleteAllow = false;
        const itemRows = this.cashReceiptRawItem.items.filter((d) => d.crudAction !== ActionConstants.DELETE);
        if (itemRows.length > 1) {
            this.isDeleteAllow = true;
        }
    }

    private prepareGLAccountColumns(): void {
        this.glAccountColumns = [
            { dataField: 'glAccountNumber', dataType: 'string', caption: 'GL Account No' },
            { dataField: 'title', dataType: 'string', caption: 'Title' },
            { dataField: 'displayText', dataType: 'string', caption: 'Display Text', visible: false }
        ];
    }

    private checkLineItemValidity(modifiedLineItems: CashReceiptItemRawDto[]): any {
        const resultStatus = { isValid: true, message: null };

        modifiedLineItems.forEach((data, index) => {
            if (Utils.notEqualsIgnoreCase(data.crudAction, ActionConstants.DELETE)) {
                const itemIndex = index + 1;
                if (!data.glAccountNumber) {
                    resultStatus.isValid = false;
                    resultStatus.message = `The GL Account number is Required for ItemId #${itemIndex}`;
                }
                this.dimensionSource.forEach((dimension) => {
                    if (dimension.required) {
                        const itemDimension = data.dimensions.find(
                            (d) => d.dimensionCode.toLowerCase() === dimension.code.toLowerCase()
                        );
                        if (!!itemDimension && !itemDimension.dimensionOptionCode) {
                            resultStatus.isValid = false;
                            resultStatus.message = `${dimension.code} is Required for ItemId #${itemIndex}`;
                        }
                    }
                });
            }
        });
        return resultStatus;
    }
}
